import { useEffect, useState } from "react";
import { QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { ShareActions, ShareQR } from "@/components/simulation/SimShare";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const LABELS = { qr: "QR code", copy: "Lien copié", share_native: "Partage natif", direct: "Direct" };

/** Carte « Lien court & QR » : code interne /l/<code>, QR téléchargeable, tunnel par source (clics → visites → simulations → leads → rappels). */
export function ShareSimulatorCard({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) return;
    api.get("/me/share-link").then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Erreur"));
  }, [slug]);
  if (!slug) return null;
  if (error) return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500" data-testid="share-card-error">{error}</div>;
  if (!data) return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-400 animate-pulse" data-testid="share-card-loading">Chargement du lien court…</div>;
  const base = `${ORIGIN}/l/${data.code}`;
  const share = { code: data.code, base, url: (s) => `${base}?s=${s}` };
  const rows = ["qr", "copy", "share_native", "direct"].map((k) => ({ k, label: LABELS[k], clics: data.clicks?.[k] || 0, ...(data.funnel?.[k] || {}) }));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="share-card">
      <div className="flex items-center gap-2 mb-1">
        <QrCode size={18} className="text-[color:var(--cf-primary)]" />
        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[color:var(--cf-primary-text)]">Lien court & QR code</div>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-4">À imprimer sur vos cartes de visite et flyers. Chaque source est mesurée séparément ; l'URL canonique <span className="font-mono">/s/{slug}</span> reste inchangée.</p>
      <div className="flex flex-col sm:flex-row gap-5">
        <ShareQR share={share} size={160} fileName={`simulateur-${slug}-qr`} testid="share-card-qr" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[14px] font-bold text-slate-900 break-all rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3" data-testid="share-card-url">{base.replace(/^https?:\/\//, "")}</div>
          <ShareActions share={share} compact title="Mon simulateur de financement immobilier" testid="share-card" />
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="share-card-stats">
              <thead><tr className="text-left text-slate-500"><th className="font-semibold py-1">Source</th><th className="font-semibold py-1 text-right">Clics</th><th className="font-semibold py-1 text-right">Visites</th><th className="font-semibold py-1 text-right">Simulations</th><th className="font-semibold py-1 text-right">Leads</th><th className="font-semibold py-1 text-right">Rappels</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.k} className="border-t border-slate-100" data-testid={`share-card-row-${r.k}`}>
                    <td className="py-1.5 font-semibold text-slate-800">{r.label}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.clics}</td><td className="py-1.5 text-right tabular-nums">{r.visites || 0}</td>
                    <td className="py-1.5 text-right tabular-nums">{r.simulations || 0}</td><td className="py-1.5 text-right tabular-nums font-bold text-[color:var(--cf-primary-text)]">{r.leads || 0}</td><td className="py-1.5 text-right tabular-nums">{r.rappels || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
